import { getEvmClientUniversal } from '@1delta/providers'
import { toGenericPriceKey, toOracleKey } from '../../assets'
import { GenericTokenList } from '../../types'
import { MulticallRetryFunction } from '../../types/providers'
import { apyToAprPercent, parseRawAmount } from '../../utils/parsing'
import { curatorNameFromVaultName } from '../curatorName'
import {
  eulerEntityCuratorName,
  fetchEulerEntityNames,
} from './entityLabels'
import { deriveConvertToAssets } from '../sharePrice'
import { fetchShareDecimals } from './fetchFromSubgraph'
import { EulerEarnVault, EulerEarnVaults } from './types'

/**
 * Euler Data API (V3) fallback for Earn vaults — used when the Goldsky
 * subgraph is stale (see `isEulerEarnSubgraphStale`) or errors. The detail
 * endpoint serves the same computed figures we otherwise derive on our side:
 * `exchangeRate` (→ convertToAssets), `availableAssets` (→ cap-aware
 * liquidity), `totalShares`, and `supplyApy`.
 *
 * See [[euler-v3-data-api]].
 */

const EULER_DATA_API_BASE = 'https://v3.euler.finance/v3'
const API_TIMEOUT_MS = 10_000
const DETAIL_CONCURRENCY = 8

// The list endpoint's `visibility` filter DEFAULTS to `visible,warning`,
// silently excluding `hidden` / `pending_review`. When Euler's automated
// config vetting delisted every Earn vault (Aug 2026), the default query
// returned an empty list and this provider went dark with no error. Request
// every state explicitly and surface the status per vault instead.
const VISIBILITY_ALL = 'visible,warning,hidden,pending_review'

/** Listed on the Euler frontend — everything else is delisted-but-live. */
const isListedStatus = (status: string | undefined): boolean =>
  status === undefined || status === 'visible' || status === 'warning'

// Coarse per-chain block time (seconds) for translating subgraph block-lag
// into wall-clock lag. Rough values are fine — the gap between a healthy
// subgraph (seconds behind) and a stalled one (days/weeks) is enormous.
const BLOCK_TIME_SEC: Record<string, number> = {
  '1': 12,
  '56': 0.75,
  '130': 1,
  '146': 0.5,
  '8453': 2,
  '1923': 2,
  '42161': 0.25,
  '43114': 2,
}
const DEFAULT_BLOCK_TIME_SEC = 2
// Treat the subgraph as stale once it lags chain head by more than this.
const MAX_LAG_SECONDS = 30 * 60

function safeBigInt(v: string | number | null | undefined): bigint {
  if (v == null || v === '') return 0n
  try {
    return BigInt(typeof v === 'number' ? Math.trunc(v) : v)
  } catch {
    return 0n
  }
}

/**
 * The Euler Data API reports `exchangeRate` as a FIXED 1e18-base rate
 * (= `convertToAssets(1e18)`), independent of the vault's share decimals.
 * Rescale it to `convertToAssets(10^shareDecimals)` — the asset-scaled
 * "1 whole share → X underlying" our pipeline stores. Identity for the
 * common 18-dec vaults; for a 6-dec share it divides by 1e12.
 */
function rescaleExchangeRate(
  exchangeRate: string | number,
  shareDecimals: number,
): string {
  const r = safeBigInt(exchangeRate)
  if (r === 0n) return '0'
  const dec = Math.max(0, Math.round(Number(shareDecimals) || 0))
  return ((r * 10n ** BigInt(dec)) / 10n ** 18n).toString()
}

async function getJson(url: string): Promise<any | undefined> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), API_TIMEOUT_MS)
  try {
    const res = await fetch(url, { signal: controller.signal })
    if (!res.ok) return undefined
    return await res.json()
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}

/**
 * True when the Goldsky subgraph at `subgraphUrl` lags chain head by more
 * than `MAX_LAG_SECONDS`. Fails *closed*: when staleness can't be determined
 * (RPC/meta unavailable) it returns `true` so we prefer the live Data API
 * rather than silently trusting a possibly-ancient subgraph. The Euler V2
 * Goldsky subgraphs are deprecated and have been observed weeks behind chain
 * head; the probe's own RPC (`getEvmClientUniversal`) uses default endpoints
 * that can't be overridden by the caller, so an unreachable head must not be
 * read as "fresh". If the API is also down, the dispatcher still falls back to
 * whatever the subgraph returned, so this never loses data.
 */
export async function isEulerEarnSubgraphStale(
  chainId: string,
  subgraphUrl: string,
): Promise<boolean> {
  const [subHead, chainHead] = await Promise.all([
    fetchSubgraphHead(subgraphUrl),
    getEvmClientUniversal({ chain: chainId, rpcId: 0 })
      .getBlockNumber()
      .then((b) => Number(b))
      .catch(() => null),
  ])

  if (subHead == null || chainHead == null) return true

  const lagBlocks = chainHead - subHead
  if (lagBlocks <= 0) return false
  const lagSeconds =
    lagBlocks * (BLOCK_TIME_SEC[chainId] ?? DEFAULT_BLOCK_TIME_SEC)
  return lagSeconds > MAX_LAG_SECONDS
}

async function fetchSubgraphHead(subgraphUrl: string): Promise<number | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), API_TIMEOUT_MS)
  try {
    const res = await fetch(subgraphUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: '{ _meta { block { number } } }' }),
      signal: controller.signal,
    })
    if (!res.ok) return null
    const json = (await res.json()) as {
      data?: { _meta?: { block?: { number?: number } } }
    }
    const n = json?.data?._meta?.block?.number
    return typeof n === 'number' ? n : null
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

interface ApiEarnVaultDetail {
  address: string
  name?: string
  symbol?: string
  decimals?: number
  asset?: { address?: string; symbol?: string; decimals?: number }
  totalAssets?: string
  totalShares?: string
  exchangeRate?: string
  totalSupplyUsd?: number
  availableAssets?: string
  availableAssetsUsd?: number
  supplyApy?: number
  apyCurrent?: number
  // Newer detail shape nests owner/curator/guardian/feeReceiver + timelock
  // under `governance`/`management`; the flat fields below are kept as a
  // fallback for older snapshots.
  governance?: {
    owner?: string
    curator?: string
    guardian?: string
    feeReceiver?: string
    timelock?: number
  }
  management?: {
    owner?: string
    curator?: string
    guardian?: string
    feeRecipient?: string
    timelockSeconds?: number
  }
  owner?: string
  curator?: string
  guardian?: string
  feeReceiver?: string
  performanceFee?: string | number
}

function mapApiDetail(
  d: ApiEarnVaultDetail,
  chainId: string,
  prices: { [asset: string]: number },
  tokenList: GenericTokenList,
  shareDecimalsByVault: Map<string, number>,
  entityNames: Map<string, string>,
  visibilityStatus?: string,
): EulerEarnVault | null {
  const address = (d.address ?? '').toLowerCase()
  const underlying = (d.asset?.address ?? '').toLowerCase()
  if (!address || !underlying) return null

  const assetMeta = tokenList[underlying]
  const assetDecimals = Number(
    d.asset?.decimals ?? assetMeta?.decimals ?? 18,
  )
  // SHARE decimals — the earn vault's own `decimals()`. The Data API's
  // `decimals` field is unreliable (reports a flat 18 even for 6-dec-asset
  // vaults), which would inflate `convertToAssets`/`exchangeRate` by
  // 10^(18 - assetDecimals) and blow up the derived share price. So we read
  // it on-chain (same as the subgraph path); fall back to asset decimals
  // (Euler Earn is offset-free — verified across mainnet/Base/Monad) when
  // the read is unavailable. Never trust `d.decimals`.
  const decimals = shareDecimalsByVault.get(address) ?? assetDecimals

  const totalAssetsRaw = String(d.totalAssets ?? '0')
  const totalSupplyRaw = String(d.totalShares ?? '0')
  // We want `convertToAssets(10^shareDecimals)` (assets per 1 whole share,
  // asset-scaled). The API's `exchangeRate` is a FIXED 1e18-base rate
  // (= `convertToAssets(1e18)`), independent of share decimals — so for a
  // non-18-dec share it must be rescaled by `10^shareDecimals / 1e18`
  // (identity for 18-dec vaults). Fall back to the totals ratio otherwise.
  const convertToAssets =
    d.exchangeRate != null
      ? rescaleExchangeRate(d.exchangeRate, decimals)
      : deriveConvertToAssets(totalAssetsRaw, totalSupplyRaw, decimals)

  // The API reports `supplyApy` as an APY in percent. Our `supplyRate` field is
  // APR in percent — convert, mirroring the Morpho path (`apyToAprPercent`).
  const supplyRate = apyToAprPercent(
    Number(d.supplyApy ?? d.apyCurrent ?? 0) || 0,
  )

  // `availableAssets` is the cap-aware withdrawable amount, the same quantity
  // our subgraph path computes as `idle + Σ min(allocated, cash)`. Hard-cap at
  // totalAssets — the API's two figures can drift a few wei apart between
  // snapshots, and liquidity must never exceed totalAssets (same invariant the
  // subgraph parser enforces).
  const availableRaw =
    safeBigInt(d.availableAssets) || safeBigInt(totalAssetsRaw)
  const totalAssetsBig = safeBigInt(totalAssetsRaw)
  const liquidityRaw = (
    availableRaw > totalAssetsBig ? totalAssetsBig : availableRaw
  ).toString()

  // totalAssets / liquidity are underlying-denominated → format with asset decimals.
  const totalAssetsFormatted = Number(
    parseRawAmount(totalAssetsRaw, assetDecimals),
  )
  const liquidityFormatted = Number(parseRawAmount(liquidityRaw, assetDecimals))

  const oracleKey =
    toOracleKey(assetMeta?.assetGroup) ?? toGenericPriceKey(underlying, chainId)
  const priceUsd = prices[oracleKey] ?? prices[underlying] ?? 0

  // Prefer our own price map; fall back to the API's USD figures.
  const totalAssetsUsd = priceUsd
    ? totalAssetsFormatted * priceUsd
    : Number(d.totalSupplyUsd ?? 0)
  const liquidityUsd = priceUsd
    ? liquidityFormatted * priceUsd
    : Number(d.availableAssetsUsd ?? 0)

  // owner/curator/guardian/feeRecipient + timelock live under
  // `governance`/`management` in the current API; fall back to the legacy
  // flat fields for older snapshots.
  const owner = (
    d.governance?.owner ??
    d.management?.owner ??
    d.owner
  )?.toLowerCase()
  const curator = (
    d.governance?.curator ??
    d.management?.curator ??
    d.curator
  )?.toLowerCase()

  return {
    address,
    underlying,
    symbol: d.symbol ?? '',
    name: d.name ?? '',
    decimals,
    assetDecimals,
    whitelisted: isListedStatus(visibilityStatus),
    visibilityStatus,
    totalAssets: totalAssetsRaw,
    totalSupply: totalSupplyRaw,
    convertToAssets,
    supplyRate,
    rewardsRate: 0,
    depositRate: supplyRate,
    // The API doesn't surface the performance fee on this endpoint; the
    // headline APY is already net, so 0 here only affects fee display.
    fee: 0,
    timelock:
      Number(d.governance?.timelock ?? d.management?.timelockSeconds ?? 0) ||
      undefined,
    owner,
    curator,
    // Euler's own entity registry names the curator by ADDRESS — authoritative
    // when it hits; the vault-name parse only covers what it misses.
    curatorName:
      eulerEntityCuratorName(entityNames, curator, owner) ??
      curatorNameFromVaultName(d.name, assetMeta?.symbol),
    guardian: (
      d.governance?.guardian ??
      d.management?.guardian ??
      d.guardian
    )?.toLowerCase(),
    feeRecipient:
      (
        d.governance?.feeReceiver ??
        d.management?.feeRecipient ??
        d.feeReceiver
      )?.toLowerCase() || undefined,
    asset: assetMeta,
    priceUsd: priceUsd || undefined,
    totalAssetsFormatted,
    totalAssetsUsd,
    liquidity: liquidityRaw,
    liquidityFormatted,
    liquidityUsd,
  }
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array(items.length)
  let cursor = 0
  const worker = async () => {
    while (true) {
      const i = cursor++
      if (i >= items.length) break
      out[i] = await fn(items[i])
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  )
  return out
}

/**
 * Fetches all Euler Earn vaults for a chain from the Euler Data API.
 * Returns `undefined` when the list request fails (so the caller can fall
 * back to the subgraph), or a (possibly empty) map otherwise.
 */
export async function fetchEulerEarnVaultsFromApi(
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  multicallRetry?: MulticallRetryFunction,
): Promise<EulerEarnVaults | undefined> {
  const list = await getJson(
    `${EULER_DATA_API_BASE}/earn/vaults?chainId=${chainId}&limit=200&visibility=${VISIBILITY_ALL}`,
  )
  if (!list) return undefined
  const rows: { address: string; visibility?: { status?: string } }[] =
    list.data ?? []
  if (rows.length === 0) return {}

  // Visibility only rides on the LIST rows — keep it keyed for the detail merge.
  const statusByAddress = new Map<string, string>()
  for (const row of rows) {
    const status = row.visibility?.status
    if (row.address && typeof status === 'string') {
      statusByAddress.set(row.address.toLowerCase(), status)
    }
  }

  const [details, entityNames] = await Promise.all([
    mapWithConcurrency(rows, DETAIL_CONCURRENCY, (row) =>
      getJson(
        `${EULER_DATA_API_BASE}/earn/vaults/${chainId}/${row.address}?include=strategies`,
      ).then((d) => d?.data as ApiEarnVaultDetail | undefined),
    ),
    fetchEulerEntityNames(chainId),
  ])

  // Read the earn vaults' real `decimals()` on-chain — the Data API's
  // `decimals` field is unreliable (see `mapApiDetail`). Best-effort: a
  // failed/absent read falls back to asset decimals per vault.
  const vaultAddrs = details
    .map((d) => d?.address)
    .filter((a): a is string => !!a)
  const shareDecimalsByVault = await fetchShareDecimals(
    chainId,
    vaultAddrs,
    multicallRetry,
  )

  const out: EulerEarnVaults = {}
  for (const d of details) {
    if (!d) continue
    const v = mapApiDetail(
      d,
      chainId,
      prices,
      tokenList,
      shareDecimalsByVault,
      entityNames,
      statusByAddress.get((d.address ?? '').toLowerCase()),
    )
    if (v) out[v.address] = v
  }
  return out
}
